import type { ReactNode } from 'react';
import { Box, FormHelperText, Typography } from '@mui/material';
import type { ResponsiveWidth } from './types';
import {
  getResponsiveReversedWidthSx,
  getResponsiveWidthSx,
  usesSideBySideCaptions,
} from './responsiveWidthUtils';

type DetailFormFieldShellProps = {
  label: string;
  hideCaption?: boolean;
  captionsWidth?: ResponsiveWidth;
  error?: string;
  children: ReactNode;
};

export const DetailFormFieldShell = ({
  label,
  hideCaption = false,
  captionsWidth,
  error,
  children,
}: DetailFormFieldShellProps) => {
  if (hideCaption) {
    return (
      <Box sx={{ mb: 1.5, width: '100%' }}>
        {children}
        {error && (
          <FormHelperText error sx={{ ml: 0 }}>
            {error}
          </FormHelperText>
        )}
      </Box>
    );
  }

  const sideBySide = usesSideBySideCaptions(captionsWidth);

  if (!sideBySide) {
    return (
      <Box sx={{ mb: 1.5, width: '100%' }}>
        {children}
        {error && (
          <FormHelperText error sx={{ ml: 0, mt: 0.5 }}>
            {error}
          </FormHelperText>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        mb: 1.5,
        width: '100%',
      }}
    >
      <Box sx={{ ...getResponsiveWidthSx(captionsWidth), pt: 1, pr: 1 }}>
        <Typography component="div" variant="body2" fontWeight={700}>
          {label}
        </Typography>
      </Box>
      <Box sx={getResponsiveReversedWidthSx(captionsWidth)}>
        {children}
        {error && (
          <FormHelperText error sx={{ ml: 0, mt: 0.5 }}>
            {error}
          </FormHelperText>
        )}
      </Box>
    </Box>
  );
};
